// docs/155 TC1–TC2: a turn's clock is held server-side (combat::hold_turn, a pause that names no one)
// while the players read the encounter's opening or watch the last turn play back. This browser says
// it is ready to plan (`opening_read`, idempotent) once nothing is left on screen for it to read or
// watch, and says it again while the hold stands, so a call lost to a dropped link does not leave the
// table waiting on the cap. The World's opening fight says it from its own face-off (Opening.tsx).
import { useEffect, useState } from 'react';
import { useEncounter } from '../../core/encounter/session';
import { rpc } from '../../core/net/reducers';
import { usePlayback } from '../../core/stores/playback';
import { useCinematic } from '../../core/stores/cinematic';
import { useCrawl } from '../../core/stores/crawl';
import { useExplore } from '../../core/stores/explore';
import { useLinkUp } from '../../core/stores/link';
import { usePauseState, usePulse, useWorldOpening } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';

/** How long nothing must be on screen before this browser is ready (a playback or a briefing can take a beat to start). */
export const SETTLE_MS = 800;
/** How often a ready browser says so again while the turn is still held. */
export const RETRY_MS = 4000;

/** Whether this browser still has something to read or watch before it plans. */
export function busyReading(s: { playing: boolean; faceoff: boolean; crawlBrief: boolean; exploreBrief: boolean }): boolean {
  return s.playing || s.faceoff || s.crawlBrief || s.exploreBrief;
}

export function TurnHold() {
  const encounterId = useEncounter((s) => s.encounterId);
  const explore = useEncounter((s) => !!s.snapshot?.explore);
  const ready = useEncounter((s) => !!s.boardSnapshot);
  const worldId = useSession((s) => s.worldId);
  const opening = useWorldOpening(worldId);
  const pause = usePauseState(encounterId);
  // the turn the server is on (the board's snapshot lags it while a turn plays back)
  const pulse = usePulse(encounterId)?.index ?? 0;
  const held = !!pause?.paused && !pause.pausedBy;
  const up = useLinkUp();
  const playing = usePlayback((s) => s.playing);
  const faceoff = useCinematic((s) => s.faceoffPlaying);
  const crawlBrief = useCrawl((s) => s.briefOpen);
  const briefFor = useExplore((s) => s.briefFor);
  useExplore((s) => s.rev);
  const exploreBrief = explore && encounterId !== undefined
    && (briefFor === encounterId || (pulse <= 1 && !useExplore.getState().briefSeen(encounterId)));
  // the World's opening fight reads its first turn from the face-off (Opening.tsx)
  const ownFaceoff = pulse <= 1 && opening !== undefined && opening.encounterId === encounterId;
  const busy = busyReading({ playing, faceoff, crawlBrief, exploreBrief });
  const [settled, setSettled] = useState<string>();
  const key = encounterId !== undefined ? `${encounterId}:${pulse}` : undefined;
  useEffect(() => {
    setSettled(undefined);
    if (!key || !held || busy || !ready || ownFaceoff) return;
    const t = setTimeout(() => setSettled(key), SETTLE_MS);
    return () => clearTimeout(t);
  }, [key, held, busy, ready, ownFaceoff]);
  useEffect(() => {
    if (encounterId === undefined || settled !== key || !held || !up) return;
    void rpc.openingRead(encounterId);
    const t = setInterval(() => void rpc.openingRead(encounterId), RETRY_MS);
    return () => clearInterval(t);
  }, [encounterId, settled, key, held, up]);
  return null;
}
